import React, { useEffect, useState } from 'react';
import { CHART_SEQUENTIAL } from '../../lib/chartColors';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, AreaChart, Area, CartesianGrid
} from 'recharts';
import {
  Box, Laptop, Server, Monitor, Smartphone, Headphones,
  Plus, RefreshCw, Layers, ShieldCheck, AlertTriangle, Clock,
  ArrowRight, CheckCircle2, Warehouse, Activity, Wrench,
  Search, Filter, ShoppingBag, Truck, Calendar, Tag, DollarSign
} from 'lucide-react';
import { useAssetStore, Asset, AssetRequest } from '../../store/assetStore';
import { useAssetSettingsStore } from '../../store/assetSettingsStore';
import { useRbacStore } from '../../store/rbacStore';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';

export function AssetsDashboard() {
  const navigate = useNavigate();
  const { assets, requests, fetchAssets, fetchRequests } = useAssetStore();
  const { settings, fetchSettings } = useAssetSettingsStore();

  // Company-wide asset visibility (Assets › View @ Company) = admins, asset
  // managers, HR, auditors, Super Admin → full fleet dashboard. Everyone else
  // (Employee, Self scope) gets a personal view of only their own records.
  // The backend already self-scopes the data; this scopes the presentation.
  const canSeeAll = useRbacStore((s) => {
    void s.permissions; void s.roles; // re-evaluate when access loads/changes
    return s.getScope('Assets', 'Assets', 'View') === 'Company';
  });

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    fetchAssets();
    fetchRequests();
    fetchSettings();
  }, [fetchAssets, fetchRequests, fetchSettings]);

  // Derived metrics from live data
  const totalAssetsCount = assets.length;
  // Statuses are the canonical assets.assets values (In Stock / Assigned / Under Maintenance / Retired …).
  const inStockCount = assets.filter(a => a.status === 'In Stock').length;
  const inUseCount = assets.filter(a => a.status === 'Assigned').length;
  const maintenanceCount = assets.filter(a => a.status === 'Under Maintenance').length;
  // Warranty alert window comes from Asset settings (warranty_alert_threshold_days).
  const alertDays = settings?.warranty.warranty_alert_threshold_days ?? 60;
  const expiringWarrantyCount = assets.filter(a => {
    if (!a.warrantyUntil || a.status === 'Retired' || a.status === 'Disposed') return false;
    const [y, m, d] = a.warrantyUntil.split('-').map(Number);
    const diff = new Date(y, m - 1, d).getTime() - Date.now();
    return diff > 0 && diff <= alertDays * 86400000;
  }).length;
  const currency = settings?.procurement.default_currency || 'INR';
  const formatMoney = (n: number) => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n); }
    catch { return `${currency} ${n.toLocaleString()}`; }
  };
  const totalValuation = formatMoney(assets.reduce((s, a) => s + (Number(a.purchaseCost) || 0), 0));

  // Dynamic Category Breakdown
  const categoryPalette = CHART_SEQUENTIAL;
  const categoryMap = new Map<string, { count: number; totalCost: number }>();
  assets.forEach(a => {
    const cat = a.category || 'Uncategorised';
    const cur = categoryMap.get(cat) || { count: 0, totalCost: 0 };
    cur.count += 1;
    cur.totalCost += Number(a.cost || a.purchaseCost) || 0;
    categoryMap.set(cat, cur);
  });
  const assetCategoryData = Array.from(categoryMap.entries()).map(([name, val], idx) => ({
    name,
    count: val.count,
    value: formatMoney(val.totalCost),
    fill: categoryPalette[idx % categoryPalette.length],
  }));

  // Dynamic Department Distribution
  const deptMap = new Map<string, number>();
  assets.forEach(a => {
    const dept = a.department || 'Unassigned';
    deptMap.set(dept, (deptMap.get(dept) || 0) + 1);
  });
  const departmentDistribution = Array.from(deptMap.entries()).map(([dept, count], idx) => ({
    dept,
    count,
    fill: categoryPalette[idx % categoryPalette.length],
  }));

  // Requests that still need action: pending approval or approved and waiting for a device.
  const pendingRequests = requests.filter((r) => r.status === 'pending' || r.status === 'approved').slice(0, 6);

  // ---------------------------------------------------------------------------
  // Employee (Self scope) view — only the signed-in person's own records.
  // `assets` and `requests` are already self-scoped by the backend, so there is
  // nothing company-wide to compute here: no fleet valuation, no department
  // distribution, no approval actions — just "my assets" and "my requests".
  // ---------------------------------------------------------------------------
  if (!canSeeAll) {
    const myInUse = assets.filter((a) => a.status === 'Assigned').length;
    const myMaint = assets.filter((a) => a.status === 'Under Maintenance').length;
    const myOpenRequests = requests.filter((r) => r.status === 'pending' || r.status === 'approved');
    const reqStatusStyle = (s: string) =>
      s === 'approved' ? 'bg-success/10 text-success-strong' :
      s === 'rejected' ? 'bg-destructive/10 text-destructive-strong' :
      s === 'fulfilled' ? 'bg-info/10 text-info-strong' :
      'bg-warning/10 text-warning-strong';
    const assetStatusStyle = (s: string) =>
      s === 'Assigned' ? 'bg-info/10 text-info-strong' :
      s === 'Under Maintenance' ? 'bg-warning/10 text-warning-strong' :
      s === 'In Stock' ? 'bg-success/10 text-success-strong' :
      'bg-secondary text-foreground';

    return (
      <div className="p-4 space-y-4 bg-background min-h-full text-foreground text-xs font-sans">
        {/* Header */}
        <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold shrink-0">
              <Box className="size-4.5" />
            </div>
            <div className="space-y-0.5 min-w-0">
              <h1 className="tracking-tight text-foreground font-display text-page-title font-semibold truncate">
                My Assets &amp; Requests
              </h1>
              <p className="text-xs text-muted-foreground leading-relaxed truncate max-w-4xl">
                The hardware assigned to you and the status of your requests and replacements.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { fetchAssets(); fetchRequests(); toast.success('Refreshed'); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-card text-xs font-semibold hover:bg-muted transition-colors shadow-2xs"
            >
              <RefreshCw className="size-3.5" /> Refresh
            </button>
            <button
              onClick={() => navigate('/assets/requests')}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
            >
              <Plus className="size-3.5" /> Raise a Request
            </button>
          </div>
        </div>

        {/* Personal KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-4 shadow-xs flex flex-col justify-between">
            <span className="text-xs font-semibold text-muted-foreground">My Assets</span>
            <div className="flex items-baseline justify-between mt-2">
              <span className="tracking-tight text-foreground font-display text-metric">{assets.length}</span>
              <span className="text-xs font-mono text-muted-foreground">Assigned to me</span>
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 shadow-xs flex flex-col justify-between">
            <span className="text-xs font-semibold text-muted-foreground">In Use</span>
            <div className="flex items-baseline justify-between mt-2">
              <span className="tracking-tight text-info font-display text-metric">{myInUse}</span>
              <span className="text-xs font-mono text-muted-foreground">Active</span>
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 shadow-xs flex flex-col justify-between">
            <span className="text-xs font-semibold text-muted-foreground">In Maintenance</span>
            <div className="flex items-baseline justify-between mt-2">
              <span className="tracking-tight text-warning font-display text-metric">{myMaint}</span>
              <span className="text-xs font-medium text-warning">Repair</span>
            </div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 shadow-xs flex flex-col justify-between">
            <span className="text-xs font-semibold text-muted-foreground">Open Requests</span>
            <div className="flex items-baseline justify-between mt-2">
              <span className="tracking-tight text-foreground font-display text-metric">{myOpenRequests.length}</span>
              <span className="text-xs font-mono text-muted-foreground">In progress</span>
            </div>
          </div>
        </div>

        {/* My Assigned Assets */}
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between bg-card/80">
            <h2 className="text-foreground font-display text-card-title">My Assigned Assets</h2>
          </div>
          <div className="overflow-x-auto">
            {assets.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Box className="size-8 opacity-40 mb-2 mx-auto" />
                <p className="text-xs font-semibold">No assets assigned to you yet.</p>
                <p className="text-xs opacity-70 mt-0.5">Hardware issued to you will appear here.</p>
              </div>
            ) : (
              <table className="w-full text-left text-xs border-collapse">
                <thead className="border-b border-border bg-muted/30 text-xs font-semibold text-muted-foreground">
                  <tr>
                    <th className="py-2.5 px-4">Asset</th>
                    <th className="py-2.5 px-4">Category</th>
                    <th className="py-2.5 px-4">Serial</th>
                    <th className="py-2.5 px-4">Status</th>
                    <th className="py-2.5 px-4">Warranty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {assets.map((a) => (
                    <tr key={a.id} className="hover:bg-muted/40 transition-colors cursor-pointer" onClick={() => navigate(`/assets/registry/${a.asset_code || a.code}`)}>
                      <td className="py-2.5 px-4">
                        <div className="font-semibold text-foreground">{a.name || '—'}</div>
                        <div className="text-muted-foreground font-mono">{a.asset_code || a.code}</div>
                      </td>
                      <td className="py-2.5 px-4 text-muted-foreground">{a.category || '—'}</td>
                      <td className="py-2.5 px-4 text-muted-foreground font-mono">{a.serial_number || '—'}</td>
                      <td className="py-2.5 px-4">
                        <span className={cn('px-2 py-0.5 rounded text-xs font-semibold', assetStatusStyle(String(a.status)))}>{a.status}</span>
                      </td>
                      <td className="py-2.5 px-4 text-muted-foreground">{a.warrantyUntil || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* My Requests & Replacements */}
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between bg-card/80">
            <div>
              <h2 className="text-foreground font-display text-card-title">My Requests &amp; Replacements</h2>
              <p className="text-xs text-muted-foreground mt-0.5">The status of requests you have raised</p>
            </div>
            <button
              onClick={() => navigate('/assets/requests')}
              className="text-xs text-primary hover:underline flex items-center gap-1 font-semibold"
            >
              View all <ArrowRight className="size-3" />
            </button>
          </div>
          <div className="overflow-x-auto">
            {requests.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <ShoppingBag className="size-8 opacity-40 mb-2 mx-auto" />
                <p className="text-xs font-semibold">You haven't raised any requests.</p>
                <p className="text-xs opacity-70 mt-0.5">Use “Raise a Request” to ask for hardware or a replacement.</p>
              </div>
            ) : (
              <table className="w-full text-left text-xs border-collapse">
                <thead className="border-b border-border bg-muted/30 text-xs font-semibold text-muted-foreground">
                  <tr>
                    <th className="py-2.5 px-4">Request</th>
                    <th className="py-2.5 px-4">Item</th>
                    <th className="py-2.5 px-4">Priority</th>
                    <th className="py-2.5 px-4">Status</th>
                    <th className="py-2.5 px-4">Raised</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {requests.slice(0, 10).map((req: any) => (
                    <tr key={req.id} className="hover:bg-muted/40 transition-colors cursor-pointer" onClick={() => navigate('/assets/requests')}>
                      <td className="py-2.5 px-4 font-semibold text-foreground font-mono">{req.request_number || '—'}</td>
                      <td className="py-2.5 px-4 text-muted-foreground font-medium">{req.asset_type}</td>
                      <td className="py-2.5 px-4">
                        <span className={cn(
                          'px-2 py-0.5 rounded text-xs font-semibold',
                          req.priority === 'high' || req.priority === 'urgent' ? 'bg-destructive/10 text-destructive-strong' :
                          req.priority === 'medium' ? 'bg-warning/10 text-warning-strong' :
                          'bg-secondary text-foreground'
                        )}>{req.priority}</span>
                      </td>
                      <td className="py-2.5 px-4">
                        <span className={cn('px-2 py-0.5 rounded text-xs font-semibold capitalize', reqStatusStyle(String(req.status)))}>{req.status}</span>
                      </td>
                      <td className="py-2.5 px-4 text-muted-foreground">{req.createdAt ? new Date(req.createdAt).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 bg-background min-h-full text-foreground text-xs font-sans">
      {/* Header Card */}
      <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold shrink-0">
            <Box className="size-4.5" />
          </div>
          <div className="space-y-0.5 min-w-0">
            <h1 className="tracking-tight text-foreground font-display text-page-title font-semibold truncate">
              ITAM Hardware &amp; Asset Management Dashboard
            </h1>
            <p className="text-xs text-muted-foreground leading-relaxed truncate max-w-4xl">
              Real-time fleet valuation, hardware lifecycle tracking, stock availability, and requisition pipeline.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => {
              fetchAssets();
              fetchRequests();
              toast.success('Asset registry updated');
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-card text-xs font-semibold hover:bg-muted transition-colors shadow-2xs"
          >
            <RefreshCw className="size-3.5" /> Refresh
          </button>
          <button
            onClick={() => navigate('/assets/new')}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs"
          >
            <Plus className="size-3.5" /> Register Asset
          </button>
        </div>
      </div>

      {/* Top 6 KPI Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Total Assets */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-xs transition-all hover:border-border/80 flex flex-col justify-between">
          <span className="text-xs font-semibold text-muted-foreground">Total Hardware</span>
          <div className="flex items-baseline justify-between mt-2">
            <span className="tracking-tight text-foreground font-display text-metric">
              {totalAssetsCount}
            </span>
            <span className="text-xs font-mono text-muted-foreground">Active Fleet</span>
          </div>
        </div>

        {/* In Stock & Ready */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-xs transition-all hover:border-border/80 flex flex-col justify-between">
          <span className="text-xs font-semibold text-muted-foreground">In Stock &amp; Ready</span>
          <div className="flex items-baseline justify-between mt-2">
            <span className="tracking-tight text-success font-display text-metric">
              {inStockCount}
            </span>
            <span className="text-xs font-medium text-success ">Available</span>
          </div>
        </div>

        {/* Assigned & In Use */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-xs transition-all hover:border-border/80 flex flex-col justify-between">
          <span className="text-xs font-semibold text-muted-foreground">Assigned / In Use</span>
          <div className="flex items-baseline justify-between mt-2">
            <span className="tracking-tight text-info font-display text-metric">
              {inUseCount}
            </span>
            <span className="text-xs font-mono text-muted-foreground">Deployed</span>
          </div>
        </div>

        {/* Maintenance / Repair */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-xs transition-all hover:border-border/80 flex flex-col justify-between">
          <span className="text-xs font-semibold text-muted-foreground">In Maintenance</span>
          <div className="flex items-baseline justify-between mt-2">
            <span className="tracking-tight text-warning font-display text-metric">
              {maintenanceCount}
            </span>
            <span className="text-xs font-medium text-warning ">RMA / Repair</span>
          </div>
        </div>

        {/* Expiring Warranties */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-xs transition-all hover:border-border/80 flex flex-col justify-between">
          <span className="text-xs font-semibold text-muted-foreground">Expiring Warranty</span>
          <div className="flex items-baseline justify-between mt-2">
            <span className="tracking-tight text-destructive font-display text-metric">
              {expiringWarrantyCount}
            </span>
            <span className="text-xs font-medium text-destructive ">≤ {alertDays} days</span>
          </div>
        </div>

        {/* Fleet Valuation */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-xs transition-all hover:border-border/80 flex flex-col justify-between">
          <span className="text-xs font-semibold text-muted-foreground">Fleet Valuation</span>
          <div className="flex items-baseline justify-between mt-2">
            <span className="tracking-tight text-foreground font-display text-metric">
              {totalValuation}
            </span>
            <span className="text-xs font-mono text-muted-foreground">Capital</span>
          </div>
        </div>
      </div>

      {/* Visual Analytics Grid: Category Donut & Department Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Category Fleet Breakdown */}
        <div className="lg:col-span-6 rounded-lg border border-border bg-card p-5 shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-foreground font-display text-card-title">
              Hardware Fleet by Category
            </h2>
            <button
              onClick={() => navigate('/assets/categories')}
              className="text-xs text-primary hover:underline flex items-center gap-1 font-semibold"
            >
              View Categories <ArrowRight className="size-3" />
            </button>
          </div>

          {assetCategoryData.length === 0 ? (
            <div className="h-60 w-full flex flex-col items-center justify-center text-muted-foreground">
              <Box className="size-8 opacity-40 mb-2" />
              <p className="text-xs font-semibold">No assets registered yet</p>
              <p className="text-xs opacity-70">Register hardware to view category breakdown</p>
            </div>
          ) : (
            <>
              <div className="h-60 w-full flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={assetCategoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={3}
                      dataKey="count"
                    >
                      {assetCategoryData.map((entry, index) => (
                        <Cell key={`asset-cat-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--card)',
                        borderColor: 'var(--border)',
                        borderRadius: '8px',
                        fontSize: '11px',
                        color: 'var(--foreground)'
                      }}
                      formatter={(val: any, name: any) => [`${val} units`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t border-border">
                {assetCategoryData.map((cat, idx) => (
                  <div key={idx} className="p-2 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="size-2 rounded-full" style={{ backgroundColor: cat.fill }} />
                      <span className="text-xs font-semibold text-foreground truncate">{cat.name}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground font-mono">
                      <span>{cat.count} {cat.count === 1 ? "unit" : "units"}</span>
                      <span>{cat.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right: Department Asset Distribution */}
        <div className="lg:col-span-6 rounded-lg border border-border bg-card p-5 shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-foreground font-display text-card-title">
              Hardware Distribution by Department
            </h2>
            <button
              onClick={() => navigate('/assets/registry')}
              className="text-xs text-primary hover:underline flex items-center gap-1 font-semibold"
            >
              Full Registry <ArrowRight className="size-3" />
            </button>
          </div>

          {departmentDistribution.length === 0 ? (
            <div className="h-60 w-full flex flex-col items-center justify-center text-muted-foreground">
              <Layers className="size-8 opacity-40 mb-2" />
              <p className="text-xs font-semibold">No department assignments</p>
              <p className="text-xs opacity-70">Assign assets to departments to view distribution</p>
            </div>
          ) : (
            <>
              <div className="h-60 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={departmentDistribution} margin={{ top: 15, right: 15, left: -10, bottom: 15 }}>
                    <XAxis
                      dataKey="dept"
                      tick={{ fontSize: 11, fill: 'currentColor' }}
                      axisLine={{ stroke: 'rgba(150,150,150,0.2)' }}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 10, fill: 'currentColor' }}
                      axisLine={{ stroke: 'rgba(150,150,150,0.2)' }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--card)',
                        borderColor: 'var(--border)',
                        borderRadius: '8px',
                        fontSize: '11px',
                        color: 'var(--foreground)'
                      }}
                      formatter={(val: any) => [`${val} Units`, 'Count']}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {departmentDistribution.map((entry, index) => (
                        <Cell key={`dept-cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border text-xs text-muted-foreground">
                <span>Total Assets: {totalAssetsCount}</span>
                <span>Active Departments: {departmentDistribution.length}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom Table: Recent Requisitions & Quick Shortcuts */}
      <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between bg-card/80">
          <div>
            <h2 className="text-foreground font-display text-card-title">
              Recent Hardware Requisitions &amp; Approvals
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">Latest employee hardware requests awaiting review and fulfillment</p>
          </div>
          <button
            onClick={() => navigate('/assets/requests')}
            className="text-xs text-primary hover:underline flex items-center gap-1 font-semibold"
          >
            Manage Requests <ArrowRight className="size-3" />
          </button>
        </div>

        <div className="overflow-x-auto">
          {pendingRequests.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <p className="text-xs font-semibold">No open requests.</p>
              <p className="text-xs opacity-70 mt-0.5">Nothing is waiting for approval or a device.</p>
            </div>
          ) : (
            <table className="w-full text-left text-xs border-collapse">
              <thead className="border-b border-border bg-muted/30 text-xs font-semibold text-muted-foreground">
                <tr>
                  <th className="py-2.5 px-4">Requested</th>
                  <th className="py-2.5 px-4">Requester</th>
                  <th className="py-2.5 px-4">Requested Item</th>
                  <th className="py-2.5 px-4">Department</th>
                  <th className="py-2.5 px-4">Priority</th>
                  <th className="py-2.5 px-4">Status</th>
                  <th className="py-2.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pendingRequests.map((req: any) => (
                  <tr key={req.id} className="hover:bg-muted/40 transition-colors">
                    <td className="py-2.5 px-4 text-muted-foreground">{req.createdAt ? new Date(req.createdAt).toLocaleDateString() : '—'}</td>
                    <td className="py-2.5 px-4 font-semibold text-foreground">{req.requester_name}</td>
                    <td className="py-2.5 px-4 text-muted-foreground font-medium">{req.asset_type}</td>
                    <td className="py-2.5 px-4 text-muted-foreground">{req.requester_department || '—'}</td>
                    <td className="py-2.5 px-4">
                      <span className={cn(
                        "px-2 py-0.5 rounded text-xs font-semibold",
                        req.priority === 'high' ? 'bg-destructive/10 text-destructive-strong ' :
                        req.priority === 'medium' ? 'bg-warning/10 text-warning-strong ' :
                        'bg-secondary text-foreground '
                      )}>
                        {req.priority}
                      </span>
                    </td>
                    <td className="py-2.5 px-4">
                      <span className={cn(
                        "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold",
                        req.status === 'approved' ? 'bg-success/10 text-success-strong ' :
                        'bg-info/10 text-info-strong '
                      )}>
                        {req.status === 'approved' ? <CheckCircle2 className="size-3" /> : <Clock className="size-3" />}
                        {req.status === 'approved' ? 'Awaiting device' : 'Pending approval'}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <button
                        onClick={() => navigate(req.status === 'approved' ? '/assets/assign' : '/assets/approval')}
                        className="inline-flex items-center gap-1 text-primary hover:underline font-semibold text-xs"
                      >
                        Review <ArrowRight className="size-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
